import { useState, useRef, useCallback, useEffect } from "react";
import { useLocationScope, useAllowedGreenhouseIds } from "@/hooks/useLocationScope";
import { Check, FileText, Tag, Calendar, Clock, MapPin, Building2, Grid3x3, Bug, FlaskConical, User, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAssistantContext } from "@/contexts/AssistantContext";
import { useCanUseAssistant } from "@/hooks/useCanUseAssistant";
import { useAssistantIsPage } from "@/hooks/useAssistantIsPage";
import { askAssistantLater, onTaskDraft, takePendingTaskDraft } from "@/lib/assistantPendingQuestion";
import { VoiceTaskConfirmation, type VoiceTaskData } from "./VoiceTaskConfirmation";
import { VoiceModalShell } from "@/components/voice/VoiceModalShell";
import {
  InitialState,
  RecordingState,
  ProcessingState,
  SummaryState,
  EditState,
  SubmittingState,
  SuccessState,
  WarningsState,
  ModalFooter,
} from "@/components/voice/VoiceStates";
import { resolveProductAmount, resolveCaldoLiters, classifyUnit } from "@/lib/treatmentDosing";
import { resolveCustomAmount, buildCustomProductsFromParsed } from "@/lib/voiceCustomProducts";
import { buildAutoTaskTitle } from "@/lib/taskTitle";
import { withDefaultTime, needsAssigneeLookup, uniqueAssignee, withAssignee } from "@/lib/voiceCardDefaults";
import {
  openOutcomeSession,
  showOutcomeCards,
  closeOutcomeSession,
  sendVoiceOutcome,
  redictationUpgrade,
  savedTaskRefs,
  type CancelledDictation,
  type OutcomeSession,
  type VoiceOutcome,
} from "@/lib/voiceOutcome";

type PanelState =
  | "idle"
  | "warnings"
  | "recording"
  | "transcribing"
  | "interpreting"
  | "summary"
  | "editing"
  | "submitting"
  | "success";

interface ConfigCounts {
  locations: number;
  greenhouses: number;
  blocks: number;
  pests: number;
  treatments: number;
  workers: number;
}

/** Total block area (m²) for a task's selected blocks. */
async function fetchBlocksAreaM2(blockIds: string[]): Promise<number> {
  if (!blockIds || blockIds.length === 0) return 0;
  const { data: blocks } = await supabase.from("blocks").select("id, area_m2").in("id", blockIds);
  return (blocks || []).reduce((s, b) => s + (b.area_m2 || 0), 0);
}

/**
 * Enrich a CUSTOM (ad-hoc) treatment task: resolve its voice-parsed products into
 * editable product lines with computed amounts, and split off any that still need the
 * user to disambiguate (spoken name matched 0 or >1 inventory rows).
 */
export async function enrichCustomTask(task: VoiceTaskData, companyId: string): Promise<VoiceTaskData> {
  const parsed = task.parsed_products || [];
  const blockIds = task.block_ids || [];
  const totalAreaM2 = await fetchBlocksAreaM2(blockIds);
  const caldoL = resolveCaldoLiters(task.water_volume ?? null, task.water_volume_unit || "L", totalAreaM2);

  const resolvedIds = parsed.map((p) => p.product_id).filter((id): id is string => !!id);
  const invMap = new Map<string, any>();
  if (resolvedIds.length > 0) {
    const { data: inv } = await supabase
      .from("inventory")
      .select("id, product_name, unit, quantity, variant_label, units_per_package")
      .in("id", resolvedIds)
      .eq("company_id", companyId)
      .is("archived_at", null);
    for (const i of inv || []) invMap.set(i.id, i);
  }

  const { resolved, unresolved } = buildCustomProductsFromParsed(parsed, invMap, blockIds, totalAreaM2, caldoL);
  return { ...task, resolved_products: resolved, unresolved_products: unresolved, resolved_area_m2: totalAreaM2 };
}

/** Recompute a custom task's product amounts for a changed area/water (keeps the user's edits). */
export async function recomputeCustomTaskAmounts(task: VoiceTaskData, _companyId: string): Promise<VoiceTaskData> {
  const blockIds = task.block_ids || [];
  const totalAreaM2 = await fetchBlocksAreaM2(blockIds);
  const caldoL = resolveCaldoLiters(task.water_volume ?? null, task.water_volume_unit || "L", totalAreaM2);
  const products = (task.resolved_products || []).map((p: any) => {
    const calc = resolveCustomAmount(p.rate_value ?? 0, p.rate_unit || p.unit, p.stock_unit || p.unit, totalAreaM2, caldoL);
    return { ...p, amount: calc.amount, unit: calc.unit, dose_value: calc.dose_value, dose_unit: calc.dose_unit, amount_per_ha: calc.amount_per_ha, target_blocks: blockIds };
  });
  return { ...task, resolved_products: products, resolved_area_m2: totalAreaM2 };
}

/**
 * Un producto dosificado por agua (cc/100L…) sin volumen de agua no se puede calcular:
 * la cantidad sale 0.00. En ese caso el volumen es obligatorio también por voz.
 */
export function voiceCaldoMissing(tk: VoiceTaskData): boolean {
  const prods = tk.resolved_products || [];
  if (!prods.some((p) => classifyUnit((p.rate_unit as string) || p.unit) === "per_caldo")) return false;
  const caldoL = resolveCaldoLiters(tk.water_volume ?? null, tk.water_volume_unit || "L", tk.resolved_area_m2 ?? 0);
  return !(caldoL != null && caldoL > 0);
}

/** Whether a task has everything it needs to be created. */
export function isVoiceTaskComplete(tk: VoiceTaskData): boolean {
  // Every task type must target a specific greenhouse and have an assigned worker.
  if (!tk.greenhouse_id) return false;
  if (!tk.assigned_to) return false;
  if (tk.type !== "treatment") return true;
  if (tk.treatment_kind === "custom") {
    if ((tk.unresolved_products?.length ?? 0) > 0) return false;
    const prods = tk.resolved_products || [];
    if (prods.length === 0) return false;
    if (!prods.every((p) => p.product_id)) return false;
    // Con el agua puesta, además, todas las cantidades deben resolver a > 0.
    return !voiceCaldoMissing(tk) && prods.every((p) => p.amount > 0);
  }
  if (!tk.treatment_id) return false;
  return !voiceCaldoMissing(tk);
}

/** i18n key describing why a task can't be confirmed yet (null if it's complete). */
export function voiceTaskBlockerKey(tk: VoiceTaskData): string | null {
  if (isVoiceTaskComplete(tk)) return null;
  if (!tk.greenhouse_id) return "voiceTask.greenhouseRequiredMsg";
  if (!tk.assigned_to) return "voiceTask.workerRequiredMsg";
  if (tk.treatment_kind === "custom") {
    if ((tk.unresolved_products?.length ?? 0) > 0) return "voiceTask.resolveProductsTitle";
    if ((tk.resolved_products?.length ?? 0) === 0) return "voiceTask.customProductsRequired";
    // Con productos resueltos, lo que bloquea es el agua, no los productos: que el
    // mensaje lo diga (antes salía el de productos, que despistaba).
    if (voiceCaldoMissing(tk)) return "voiceTask.waterVolumeRequiredMsg";
    return "voiceTask.customProductsRequired";
  }
  if (!tk.treatment_id) return "voiceTask.treatmentRequired";
  return "voiceTask.waterVolumeRequiredMsg";
}

/** Fetch treatment_components and block areas, then enrich tasks with per-ha product calculations */
export async function enrichTasksWithProducts(
  tasks: VoiceTaskData[],
  companyId: string
): Promise<VoiceTaskData[]> {
  const enriched: VoiceTaskData[] = [];

  for (const task of tasks) {
    if (task.type === "treatment" && task.treatment_kind === "custom") {
      enriched.push(await enrichCustomTask(task, companyId));
      continue;
    }
    if (task.type !== "treatment" || !task.treatment_id) {
      enriched.push(task);
      continue;
    }

    try {
      const { data: components } = await supabase
        .from("treatment_components")
        .select(`*, inventory!inner(product_name, quantity, unit)`)
        .eq("treatment_id", task.treatment_id)
        .eq("company_id", companyId);

      if (!components || components.length === 0) {
        enriched.push(task);
        continue;
      }

      const blockIds = task.block_ids || [];
      let totalAreaM2 = 0;
      if (blockIds.length > 0) {
        const { data: blocks } = await supabase
          .from("blocks")
          .select("id, area_m2")
          .in("id", blockIds);
        totalAreaM2 = (blocks || []).reduce((s, b) => s + (b.area_m2 || 0), 0);
      }

      // Caldo: keep what the user already entered; otherwise pre-fill from the
      // recipe default. Default unit is L (total liters), how growers think.
      const { data: treatmentRow } = await supabase
        .from("treatments")
        .select("default_caldo_value, default_caldo_unit")
        .eq("id", task.treatment_id)
        .maybeSingle();
      const waterVolume = task.water_volume != null
        ? task.water_volume
        : ((treatmentRow as any)?.default_caldo_value ?? null);
      const waterVolumeUnit = task.water_volume_unit
        || (treatmentRow as any)?.default_caldo_unit
        || "L";
      const caldoL = resolveCaldoLiters(waterVolume, waterVolumeUnit, totalAreaM2);

      const products = components.map((c: any) => {
        const componentUnit = c.unit || c.inventory?.unit || "L";
        const stockUnit = c.inventory?.unit || componentUnit;
        const isPerHa = c.amount_is_per_ha !== false;
        const rate_unit = c.rate_unit || (isPerHa ? `${componentUnit}/ha` : componentUnit);
        const rate_value = c.rate_value != null ? c.rate_value : (c.amount || 1);
        const r = resolveProductAmount({ value: rate_value, unit: rate_unit, areaM2: totalAreaM2, caldoL, stockUnit });
        const basis = classifyUnit(rate_unit);

        return {
          product_id: c.product_id,
          product_name: c.inventory?.product_name || "Unknown",
          amount: r.error ? 0 : r.amount,
          unit: r.amountUnit,
          stock: c.inventory?.quantity || 0,
          is_from_treatment: true,
          rate_value,
          rate_unit,
          stock_unit: stockUnit,
          amount_per_ha: basis === "per_area" ? rate_value : null,
          dose_value: basis === "per_caldo" ? rate_value : null,
          dose_unit: basis === "per_caldo" ? rate_unit : null,
          target_blocks: blockIds,
        };
      });

      enriched.push({
        ...task,
        resolved_products: products,
        resolved_area_m2: totalAreaM2,
        water_volume: waterVolume,
        water_volume_unit: waterVolumeUnit,
      });
    } catch (err) {
      console.error("Error enriching task products:", err);
      enriched.push(task);
    }
  }

  return enriched;
}

/**
 * Color de la baldosa de cada dato del resumen: fondo sólido con el icono en
 * blanco, el mismo lenguaje que el resto de la app (ver `taskTypeTileShadow`).
 *
 * Se colorea POR GRUPO, no un color por fila: cuándo (fecha y hora) en azul,
 * dónde (ubicación, invernadero, sector) en verde, y qué se va a hacer con sus
 * colores de siempre —plaga en ámbar y tratamiento en rojo, los mismos que en
 * el catálogo—. Once colores distintos se leerían como un arcoíris; así el ojo
 * agrupa solo y de paso la plaga y el tratamiento se reconocen sin leerlos.
 */
/* Sin `as const`: con él cada color es su propio tipo literal, el array de filas
   infiere `tone` como la unión de los CUATRO primeros y los `push` de más abajo
   —plaga, trabajador, prioridad— dejan de compilar. */
const SUMMARY_TONES: Record<string, string> = {
  name: "#0FA9B8",
  type: "#6366F1",
  when: "#2B7DE0",
  where: "#16A971",
  pest: "#E0962E",
  treatment: "#E5574E",
  worker: "#8B5CF6",
  priority: "#64748B",
};

function buildSummaryRows(task: VoiceTaskData, t: (k: string, opts?: any) => string) {
  const typeLabel = task.type
    ? t(`planner.taskTypes.${task.type}`, { defaultValue: task.type })
    : "";
  const priorityLabel = task.priority
    ? t(`planner.priorities.${task.priority}`, { defaultValue: task.priority })
    : "";
  const rows = [
    { icon: FileText, label: t("voiceModal.fields.name"), value: task.title || "", tone: SUMMARY_TONES.name },
    { icon: Tag, label: t("voiceModal.fields.type"), value: typeLabel, tone: SUMMARY_TONES.type },
    { icon: Calendar, label: t("voiceModal.fields.date"), value: task.scheduled_date || "", tone: SUMMARY_TONES.when },
    { icon: Clock, label: t("voiceModal.fields.time"), value: task.scheduled_time || "", tone: SUMMARY_TONES.when },
    { icon: MapPin, label: t("voiceModal.fields.location"), value: task.location_name || "", tone: SUMMARY_TONES.where },
    { icon: Building2, label: t("voiceModal.fields.greenhouse"), value: task.greenhouse_name || "", tone: SUMMARY_TONES.where },
    { icon: Grid3x3, label: t("voiceModal.fields.section"), value: (task.block_names || []).join(", "), tone: SUMMARY_TONES.where },
  ];
  if (task.type === "treatment") {
    rows.push({ icon: Bug, label: t("voiceModal.fields.pest"), value: task.pest_name || "", tone: SUMMARY_TONES.pest });
    rows.push({
      icon: FlaskConical,
      label: t("voiceModal.fields.treatment"),
      value: task.treatment_name || "",
      tone: SUMMARY_TONES.treatment,
      // Un tratamiento sin resolver sigue avisando en ámbar: el aviso manda
      // sobre el color de la baldosa (ver SummaryRow).
      ...(task.treatment_id ? {} : { warning: true } as any),
    } as any);
  }
  rows.push({ icon: User, label: t("voiceModal.fields.worker"), value: task.worker_name || "", tone: SUMMARY_TONES.worker });
  rows.push({ icon: Flag, label: t("voiceModal.fields.priority"), value: priorityLabel, tone: SUMMARY_TONES.priority });
  return rows;
}

export function VoiceTaskPanel() {
  const { t, i18n } = useTranslation();
  const { company, user } = useAuth();
  const { allowedLocationIds, excludedBlockIds } = useLocationScope();
  const allowedGreenhouseIds = useAllowedGreenhouseIds();
  const { toast } = useToast();
  const { setIsPanelOpen, isPanelOpen, activePanel, setActivePanel } = useAssistantContext();
  const canChat = useCanUseAssistant();
  const assistantIsPage = useAssistantIsPage();
  const navigate = useNavigate();

  const [state, setState] = useState<PanelState>("idle");
  const [tasksData, setTasksData] = useState<VoiceTaskData[]>([]);
  const [transcript, setTranscript] = useState("");
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);
  const [configCounts, setConfigCounts] = useState<ConfigCounts | null>(null);
  const [missingItems, setMissingItems] = useState<string[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  /* CÓMO ACABÓ EL DICTADO (final_result, anexo B §3.3). Un dictado = una sesión:
     se abre cuando el edge devuelve `voice_log_id` y se cierra UNA vez —al
     confirmar, al cerrar o empezar de nuevo, o al volver a grabar con él
     pendiente—. Va por refs para que ningún cierre dependa de un render viejo, y
     se manda sin esperar: registrar nunca puede estorbar a crear la tarea. */
  const outcomeRef = useRef<OutcomeSession | null>(null);
  const tasksRef = useRef<VoiceTaskData[]>([]);
  tasksRef.current = tasksData;
  /* El último dictado que se cerró como «cancelled» (empezar de nuevo, cerrar).
     Si a los pocos minutos se graba otro, se corrige a «redictated»: desde el
     resumen no hay otro camino para volver a dictar, y sin esto los re-dictados
     reales (83f689c1 rn591 → 2bc49c37 rn592) contaban como abandonos. */
  const lastCancelledRef = useRef<CancelledDictation | null>(null);
  const reportOutcome = useCallback(
    (outcome: VoiceOutcome, extra: { taskIds?: string[]; titles?: string[] } = {}) => {
      const shownAt = outcomeRef.current?.shownAt ?? 0;
      const payload = closeOutcomeSession(outcomeRef.current, outcome, {
        cards: tasksRef.current,
        taskIds: extra.taskIds,
        titles: extra.titles,
        now: Date.now(),
      });
      outcomeRef.current = null;
      if (payload) lastCancelledRef.current = outcome === "cancelled" ? { payload, shownAt } : null;
      sendVoiceOutcome((body) => supabase.functions.invoke("voice-log-outcome", { body }), payload);
    },
    [],
  );
  /* Tarea preparada en el chat (draft_task): al abrirse este panel, o si ya está
     abierto, el texto se interpreta como un dictado — misma tarjeta, mismo «Confirmar».
     Se guarda la función en una ref para que el efecto no dependa de un render viejo. */
  const interpretRef = useRef<((text: string) => void) | null>(null);
  useEffect(() => {
    if (!isPanelOpen || activePanel !== "taskVoice" || !company?.id) return;
    const pending = takePendingTaskDraft();
    if (pending) interpretRef.current?.(pending);
    return onTaskDraft((text) => {
      takePendingTaskDraft();
      interpretRef.current?.(text);
    });
  }, [isPanelOpen, activePanel, company?.id]);

  // Se desmonta con un dictado a medias (se navega fuera): cuenta como cancelado.
  useEffect(() => () => reportOutcome("cancelled"), [reportOutcome]);

  // Reset local state when modal closes or tab switches away
  useEffect(() => {
    if (!isPanelOpen || activePanel !== "taskVoice") {
      reportOutcome("cancelled");
      setState("idle");
      setTasksData([]);
      setTranscript("");
      setCreatedTaskId(null);
    }
    // `reportOutcome` es estable (useCallback sin dependencias): no añade disparos.
  }, [isPanelOpen, activePanel, reportOutcome]);

  useEffect(() => {
    if (!company?.id) return;
    const companyId = company.id;
    Promise.all([
      supabase.from("locations").select("id", { count: "exact", head: true }).eq("company_id", companyId),
      supabase.from("greenhouses").select("id", { count: "exact", head: true }).eq("company_id", companyId),
      supabase.from("blocks").select("id", { count: "exact", head: true }).eq("company_id", companyId).is("archived_at", null),
      supabase.from("pests").select("id", { count: "exact", head: true }).or(`company_id.eq.${companyId},company_id.is.null`),
      supabase.from("treatments").select("id", { count: "exact", head: true }).eq("company_id", companyId),
      supabase.from("profiles").select("user_id", { count: "exact", head: true }).eq("company_id", companyId),
    ]).then(([loc, gh, bl, pe, tr, wo]) => {
      setConfigCounts({
        locations: loc.count || 0,
        greenhouses: gh.count || 0,
        blocks: bl.count || 0,
        pests: pe.count || 0,
        treatments: tr.count || 0,
        workers: wo.count || 0,
      });
    });
  }, [company?.id]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((tk) => tk.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await processAudio(blob);
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      // Se vuelve a grabar con el dictado anterior sin cerrar: se re-dicta. Y si
      // se acababa de cancelar (dentro de la ventana), también.
      reportOutcome("redictated");
      const upgrade = redictationUpgrade(lastCancelledRef.current, Date.now());
      lastCancelledRef.current = null;
      sendVoiceOutcome((body) => supabase.functions.invoke("voice-log-outcome", { body }), upgrade);
      setState("recording");
    } catch {
      toast({ title: t("ipmAssistant.micPermissionError"), variant: "destructive" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, toast, reportOutcome]);

  const checkConfigAndRecord = useCallback(() => {
    if (!configCounts) {
      startRecording();
      return;
    }
    const missing: string[] = [];
    if (configCounts.locations === 0) missing.push(t("voiceTask.missingLocations"));
    if (configCounts.greenhouses === 0) missing.push(t("voiceTask.missingGreenhouses"));
    if (configCounts.blocks === 0) missing.push(t("voiceTask.missingBlocks"));
    if (configCounts.pests === 0) missing.push(t("voiceTask.missingPests"));
    if (configCounts.treatments === 0) missing.push(t("voiceTask.missingTreatments"));
    if (configCounts.workers === 0) missing.push(t("voiceTask.missingWorkers"));
    if (missing.length > 0) {
      setMissingItems(missing);
      setState("warnings");
    } else {
      startRecording();
    }
  }, [configCounts, t, startRecording]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  /**
   * Del TEXTO a las tarjetas: la mitad de un dictado que no depende del audio. La usan
   * el micrófono (tras transcribir) y el chat del asistente (draft_task: la tarea se
   * prepara hablando y la tarjeta es la MISMA, con su «Confirmar»). channel: 'chat'
   * queda en el registro del dictado para medir los dos caminos por separado.
   */
  const interpretTranscript = async (
    text: string,
    opts: { audioUrl: string | null; spokenLanguage: string | null; channel: "voice" | "chat" },
  ) => {
    if (!company?.id) return;
    const { audioUrl, spokenLanguage, channel } = opts;
    try {
      setTranscript(text);
      setState("interpreting");
      const { data: interpretData, error: interpretError } = await supabase.functions.invoke(
        "voice-create-task",
        {
          body: {
            transcript: text,
            company_id: company.id,
            language: i18n.language,
            spoken_language: spokenLanguage,
            // La zona del móvil: «hoy», «mañana» y «el viernes» en su día, no en UTC del servidor.
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            audio_url: audioUrl,
            channel,
          },
        }
      );
      if (interpretError) throw interpretError;

      /* El log ya existe: su resultado se registrará pase lo que pase (también si
         no salen tarjetas). Solo si el edge devuelve su id (parte 2); sin id, no se
         envía nada. */
      outcomeRef.current = openOutcomeSession(interpretData?.voice_log_id, [], Date.now());

      let tasks: VoiceTaskData[] = interpretData?.tasks;
      /* DV3 — lo dictado no era un encargo (ruido, «voy a cancelar», una observación…):
         ninguna tarjeta. Si era una PREGUNTA, al asistente con ella ya escrita. El
         motor v1 nunca manda `no_task_reason`: con él todo sigue como antes. */
      const noTask: string | null = typeof interpretData?.no_task_reason === "string" ? interpretData.no_task_reason : null;
      if (noTask && (!Array.isArray(tasks) || tasks.length === 0)) {
        reportOutcome("cancelled");
        setState("idle");
        // Desde el chat no se rebota al chat: allí ya se está hablando.
        if (noTask === "question" && canChat && channel === "voice") {
          askAssistantLater(text);
          if (assistantIsPage) navigate("/advanced");
          else setActivePanel("aiChat");
        } else {
          toast({ title: t("voiceTask.emptyTranscript") });
        }
        return;
      }
      if (!tasks || !Array.isArray(tasks) || tasks.length === 0) throw new Error("No task data returned");

      // Los defaults de la tarjeta viven en src/lib/voiceCardDefaults.ts (el corpus
      // de voz usa los mismos) y quedan anotados en `card_defaults`.
      const now = new Date();
      tasks = tasks.map((tk) => withDefaultTime(tk, now));
      tasks = await enrichTasksWithProducts(tasks, company.id);

      // Auto-assign worker
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        if (!needsAssigneeLookup(task)) continue;
        try {
          const { data: assignments } = await supabase
            .from("block_worker_assignments")
            .select("user_id")
            .eq("company_id", company.id)
            .in("block_id", task.block_ids)
            .eq("is_active", true);
          const userId = uniqueAssignee(assignments);
          if (userId) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("full_name")
              .eq("user_id", userId)
              .single();
            tasks[i] = withAssignee(task, userId, profile?.full_name);
          }
        } catch (err) {
          console.error("Auto-assign worker error:", err);
        }
      }

      setTasksData(tasks);
      showOutcomeCards(outcomeRef.current, tasks, Date.now());
      setState("summary");
    } catch (err: any) {
      console.error("Voice task error:", err);
      toast({ title: t("voiceTask.errorProcessing"), variant: "destructive" });
      setState("idle");
    }
  };

  interpretRef.current = (text: string) => {
    reportOutcome("redictated");
    void interpretTranscript(text, { audioUrl: null, spokenLanguage: null, channel: "chat" });
  };

  const processAudio = async (blob: Blob) => {
    if (!company?.id) return;
    setState("transcribing");
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");
      formData.append("language", i18n.language);
      formData.append("company_id", company.id);

      const { data: session } = await supabase.auth.getSession();
      const transcribeRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-transcribe`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session?.session?.access_token}` },
          body: formData,
        }
      );

      if (!transcribeRes.ok) {
        /* El motivo real, en la consola. «Transcription failed» a secas no
           distingue entre una clave mala, un plan sin transcripción y un audio
           corrupto, y eran horas de adivinar cuál de las tres era. */
        const detail = await transcribeRes.text().catch(() => "");
        console.error("elevenlabs-transcribe failed:", transcribeRes.status, detail);
        throw new Error(`Transcription failed (${transcribeRes.status}) ${detail}`.trim());
      }
      // `language`: el idioma que DETECTA la transcripción (se descartaba): título y
      // notas salen en el idioma hablado, no en el de la interfaz (anexo B, R-idioma).
      const { text, audio_url, language: spokenLanguage } = await transcribeRes.json();
      if (!text?.trim()) {
        toast({ title: t("voiceTask.emptyTranscript"), variant: "destructive" });
        setState("idle");
        return;
      }
      await interpretTranscript(text, {
        audioUrl: audio_url || null,
        spokenLanguage: typeof spokenLanguage === "string" ? spokenLanguage : null,
        channel: "voice",
      });
    } catch (err: any) {
      console.error("Voice task error:", err);
      toast({ title: t("voiceTask.errorProcessing"), variant: "destructive" });
      setState("idle");
    }
  };

  const handleConfirm = async () => {
    if (!company?.id || !user?.id || tasksData.length === 0) return;

    const hasUnresolvedConflict = tasksData.some(
      (tt) =>
        (tt.location_warning?.type === "location_conflict" || tt.location_warning?.type === "greenhouse_not_in_location") &&
        (!tt.greenhouse_id || (tt.block_ids?.length ?? 0) === 0)
    );
    if (hasUnresolvedConflict) {
      toast({ title: t("voiceTask.locationConflictSubmitBlocked"), variant: "destructive" });
      return;
    }

    // Treatment products must resolve to a real amount. Amount 0 means the
    // calculation is missing data (no section for L/ha, or no caldo for cc/100L).
    // Block rather than create a task that deducts nothing/wrong. Covers both a
    // configured recipe and an ad-hoc custom product list.
    const unresolvedTask = tasksData.find(tk =>
      tk.type === "treatment" && (tk.treatment_id || tk.treatment_kind === "custom") &&
      (tk.resolved_products || []).length > 0 &&
      (tk.resolved_products || []).some((p: any) => !(p.amount > 0))
    );
    if (unresolvedTask) {
      const needsCaldo = (unresolvedTask.resolved_products || []).some(
        (p: any) => p.dose_unit && !(p.amount > 0)
      );
      toast({
        title: t("voiceTask.treatmentRequired"),
        description: needsCaldo
          ? "Indica el volumen de caldo para los productos en cc/100L."
          : "Falta la sección para calcular los litros por hectárea. Selecciónala en la tarea.",
        variant: "destructive",
      });
      return;
    }

    // Generic completeness: a configured task needs a treatment; a custom task needs at
    // least one resolved product and no pending disambiguation.
    const incompleteTask = tasksData.find(tk => !isVoiceTaskComplete(tk));
    if (incompleteTask) {
      toast({ title: t(voiceTaskBlockerKey(incompleteTask)!), variant: "destructive" });
      return;
    }

    /* Y NADIE MANDA TRABAJO A UNA FINCA QUE NO LLEVA (02-09-2026).
       El desplegable de la pantalla de revisión ya solo ofrece lo suyo, pero el
       dictado resuelve la finca de lo que se ha dicho en voz alta: decir
       «Campohermoso» la plantaba ahí sin tocar ningún desplegable. Esto lo para
       antes de escribir y con un mensaje que se entiende; lo que lo impide de
       verdad es la política de INSERT de la base de datos, porque hay cuatro
       caminos para crear una tarea y este es uno. */
    /* Y SE MIRA EL INVERNADERO, no solo la finca (02-09-2026). El dictado puede
       resolver «Nave 5» sin decir de qué finca es, y entonces `location_id` se
       queda a null: mirando solo la finca, una tarea a un invernadero ajeno
       pasaba este corte y se estrellaba contra la RLS con un error que no
       explica nada. */
    const fueraDeAlcance = tasksData.find(
      (tk) =>
        (allowedLocationIds && tk.location_id && !allowedLocationIds.has(tk.location_id)) ||
        (allowedGreenhouseIds && tk.greenhouse_id && !allowedGreenhouseIds.includes(tk.greenhouse_id)) ||
        (excludedBlockIds?.size && (tk.block_ids || []).some((b: string) => excludedBlockIds.has(b))),
    );
    if (fueraDeAlcance) {
      toast({
        title: t(
          "voiceTask.outOfScope",
          "No puedes crear tareas en {{finca}}: no está entre tus fincas.",
          { finca: fueraDeAlcance.location_name || fueraDeAlcance.greenhouse_name || "—" },
        ),
        variant: "destructive",
      });
      return; // se queda en la pantalla de revisión, como los demás cortes
    }

    setState("submitting");

    try {
      let firstId: string | null = null;
      const insertedIds: string[] = [];
      const savedTitles: string[] = [];
      for (const task of tasksData) {
        const blockIds = task.block_ids || [];
        const hasMultipleBlocks = blockIds.length > 1;

        let customProducts: any[] | null = null;
        if (task.resolved_products && task.resolved_products.length > 0) {
          customProducts = task.resolved_products.map((p: any, idx: number) => ({
            product_id: p.product_id,
            product_name: p.product_name,
            amount: p.amount,
            unit: p.unit,
            stock: p.stock,
            is_from_treatment: p.is_from_treatment ?? false,
            rate_value: p.rate_value,
            rate_unit: p.rate_unit,
            stock_unit: p.stock_unit,
            amount_per_ha: p.amount_per_ha,
            dose_value: p.dose_value,
            dose_unit: p.dose_unit,
            target_blocks: blockIds,
            ...(idx === 0 && task.water_volume != null
              ? { water_volume: task.water_volume, water_volume_unit: task.water_volume_unit || "L" }
              : {}),
          }));
        } else if (hasMultipleBlocks) {
          customProducts = [{ target_blocks: blockIds }];
          if (task.observed_rows) customProducts[0].observed_rows = task.observed_rows;
        }

        const taskInsert: any = {
          company_id: company.id,
          created_by: user.id,
          status: "pending",
          /* La regla de títulos, otra vez al guardar y para TODAS (usuario
             14-09-2026): con varias tareas dictadas, las que no se abrían conservaban
             el título que inventó la IA, ubicación incluida. Un título escrito a mano
             se respeta. */
          title: task.title_customized
            ? task.title
            : buildAutoTaskTitle(
                {
                  type: task.type,
                  pestName: task.pest_name,
                  treatmentName: task.treatment_name,
                  customTreatment: task.treatment_kind === "custom",
                },
                t,
              ),
          type: task.type,
          priority: task.priority,
          greenhouse_id: task.greenhouse_id || null,
          block_id: hasMultipleBlocks ? null : (blockIds[0] || null),
          // Plaga y receta con la MISMA regla que registra final_result (savedTaskRefs).
          ...savedTaskRefs(task),
          assigned_to: task.assigned_to || null,
          scheduled_date: new Date(task.scheduled_date + "T" + (task.scheduled_time || "08:00")).toISOString(),
          notes: task.notes || null,
          custom_products: customProducts,
        };

        const { data: inserted, error } = await supabase
          .from("planner_tasks")
          .insert(taskInsert)
          .select("id")
          .single();
        if (error) throw error;
        if (!firstId && inserted?.id) firstId = inserted.id;
        insertedIds.push(inserted?.id ?? "");
        savedTitles.push(taskInsert.title);
      }

      // Tarjeta k → tarea insertada k. Sin esperar a la respuesta.
      reportOutcome("confirmed", { taskIds: insertedIds, titles: savedTitles });
      setCreatedTaskId(firstId);
      setState("success");
      const count = tasksData.length;
      toast({
        title: count > 1 ? t("voiceTask.tasksCount", { count }) : t("voiceTask.taskCreated"),
      });
    } catch (err: any) {
      console.error("Task insert error:", err);
      toast({ title: t("voiceTask.errorCreating"), description: err.message, variant: "destructive" });
      setState("editing");
    }
  };

  const resetToInitial = () => {
    // Empezar de nuevo con un dictado sin confirmar = cancelarlo (tras confirmar ya
    // está registrado y esto no manda nada).
    reportOutcome("cancelled");
    setState("idle");
    setTasksData([]);
    setTranscript("");
    setCreatedTaskId(null);
  };

  const handleViewTask = () => {
    setIsPanelOpen(false);
    const dateStr = tasksData[0]?.scheduled_date;
    if (createdTaskId) {
      const params = new URLSearchParams({ taskId: createdTaskId });
      if (dateStr) params.set("date", dateStr);
      navigate(`/planner?${params.toString()}`);
    } else {
      navigate("/planner");
    }
  };

  const handleTaskDataChange = async (index: number, updated: VoiceTaskData) => {
    const prev = tasksData[index];
    const treatmentChanged = prev.treatment_id !== updated.treatment_id && updated.treatment_id;
    // Re-resolve the breakdown when the location (area) changes too, so it
    // recomputes instead of staying hidden after the user picks a block.
    const blocksChanged = (prev.block_ids || []).join(",") !== (updated.block_ids || []).join(",");
    const greenhouseChanged = prev.greenhouse_id !== updated.greenhouse_id;
    setTasksData(prev2 => prev2.map((tk, i) => i === index ? updated : tk));

    // Custom (ad-hoc) task: recompute product amounts for the new area without rebuilding
    // from the voice parse (preserves the manager's edits).
    if (updated.type === "treatment" && updated.treatment_kind === "custom" && (blocksChanged || greenhouseChanged) && company?.id) {
      const recomputed = await recomputeCustomTaskAmounts(updated, company.id);
      setTasksData(prev2 => prev2.map((tk, i) => i === index ? recomputed : tk));
      return;
    }

    const needsReenrich =
      !!updated.treatment_id &&
      updated.type === "treatment" &&
      updated.treatment_kind !== "custom" &&
      (treatmentChanged || blocksChanged || greenhouseChanged);
    if (needsReenrich && company?.id) {
      const enriched = await enrichTasksWithProducts([updated], company.id);
      if (enriched[0]) {
        setTasksData(prev2 => prev2.map((tk, i) => i === index ? enriched[0] : tk));
      }
    }
  };

  const blockingTask = tasksData.find(tk => !isVoiceTaskComplete(tk));
  const confirmDisabled = !!blockingTask;
  const treatmentWarning = blockingTask ? t(voiceTaskBlockerKey(blockingTask)!) : undefined;

  // Footer per state
  let footer: React.ReactNode = null;
  if (state === "recording") {
    footer = (
      <ModalFooter
        primary={{ label: t("voiceModal.actions.stop"), onClick: stopRecording }}
        secondary={{ label: t("voiceModal.actions.cancel"), onClick: () => { stopRecording(); resetToInitial(); } }}
      />
    );
  } else if (state === "summary" || state === "editing") {
    footer = (
      <ModalFooter
        primary={{ label: t("voiceModal.actions.confirm"), icon: Check, onClick: handleConfirm, disabled: confirmDisabled }}
        warning={treatmentWarning}
      />
    );
  } else if (state === "success") {
    footer = (
      <ModalFooter
        primary={{ label: t("voiceModal.actions.viewTask"), onClick: handleViewTask }}
        secondary={{ label: t("voiceModal.actions.createAnother"), onClick: resetToInitial }}
      />
    );
  }

  const hideTabs = state === "recording" || state === "transcribing" || state === "interpreting" || state === "submitting";
  const confirmOnClose = state !== "idle" && state !== "success";
  /* En cuanto se empieza a grabar, la hoja se estira a pantalla completa y ya no
     se encoge en lo que dura el flujo. «Revisar detalles» es la razón: la ficha
     de la tarea, sus lápices y el pie con «Confirmar» no caben en 68dvh, así que
     se revisaba por una rendija y el aviso de qué faltaba quedaba pegado al
     borde. Vuelve a su tamaño al empezar de nuevo (idle) o al pedir permisos. */
  const expanded = state !== "idle" && state !== "warnings";

  const firstTask = tasksData[0];
  const summaryRows = firstTask ? buildSummaryRows(firstTask, t) : [];

  return (
    <VoiceModalShell tab="task" footer={footer} hideTabs={hideTabs} confirmOnClose={confirmOnClose} expanded={expanded}>
      <AnimatePresence mode="wait">
        {state === "warnings" && (
          <WarningsState
            items={missingItems}
            onContinue={() => { setState("idle"); startRecording(); }}
          />
        )}
        {state === "idle" && <InitialState flow="task" onStart={checkConfigAndRecord} />}
        {state === "recording" && <RecordingState onStop={stopRecording} />}
        {(state === "transcribing" || state === "interpreting") && (
          <ProcessingState transcript={state === "interpreting" ? transcript : undefined} />
        )}
        {(state === "summary" || state === "editing") && firstTask && (
          <EditState>
            <div className="space-y-4">
              {tasksData.length > 1 && (
                <p className="text-xs text-primary font-semibold">
                  {t("voiceTask.tasksCount", { count: tasksData.length })}
                </p>
              )}
              {tasksData.map((task, idx) => (
                <VoiceTaskConfirmation
                  key={idx}
                  taskData={task}
                  taskIndex={idx}
                  totalTasks={tasksData.length}
                  onConfirm={() => {}}
                  onRetry={resetToInitial}
                  onTaskDataChange={(u) => handleTaskDataChange(idx, u)}
                  companyId={company?.id || ""}
                />
              ))}
            </div>
          </EditState>
        )}
        {state === "submitting" && <SubmittingState />}
        {state === "success" && firstTask && (
          <SuccessState
            flow="task"
            rows={summaryRows.slice(0, 7)}
            primaryLabel={t("voiceModal.actions.viewTask")}
            onPrimary={handleViewTask}
            onSecondary={resetToInitial}
          />
        )}
      </AnimatePresence>
    </VoiceModalShell>
  );
}
